import { type Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Maximize2, Minimize2, SquarePen } from 'lucide-react';
import {
  MAX_CHAT_MESSAGE_LENGTH,
  type ChatEvent,
  type ChatMessage,
} from '@wundo/shared/chat.ts';
import type { OnboardingNudgeMessageSource } from '@wundo/shared/onboardingNudge.ts';
import {
  ChatComposer,
  ChatDisclaimer,
  ChatDrawer,
  ChatDrawerFooter,
  ChatMarkdown,
  ChatMessage as ChatMessageBubble,
  ChatMessageList,
  ChatQuickAction,
  ChatQuickActions,
  ChatRecapCard,
  IconButton,
  chatCardLinkVariants,
} from '@wundo/ui';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import {
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_FILE_BYTES,
  MAX_IMAGE_BYTES,
} from '@wundo/shared/attachments.ts';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import {
  CHAT_UPLOAD_ACCEPT,
  type ChatMessagePage,
  fetchChatMessages,
  fetchChatState,
  reportChatReceipt,
  sendChatMessage,
} from '@/lib/chat.ts';
import { type ChatAttachmentProblem, useChatAttachments } from '@/lib/chatAttachments.ts';
import { type DictationProblem, useDictation } from '@/lib/useDictation.ts';
import { audioDeviceOptions } from '@/lib/audioDeviceOptions.ts';
import { useChatSocket } from '@/lib/chatSocket.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { TYPING_TIMEOUT_MS } from '@/lib/chatTyping.ts';
import { currentChatDrawerState, setChatDrawerState } from '@/lib/chatDrawerState.ts';
import { markChatUnread, useChatUnread } from '@/lib/chatUnread.ts';
import { useChatDraft } from '@/lib/chatDraft.ts';
import { useChatOpeningAsked } from '@/lib/chatOpeningAsk.ts';
import {
  type ChatPageNudge,
  type ChatPageOffer,
  type ChatPageRecap,
  type ChatQuickActionOffer,
  useChatPageOffer,
} from '@/lib/chatPageOffer.ts';
import {
  sendMeetingDebrief,
  sendMeetingIntake,
  sendMeetingRecap,
} from '@/lib/meetingPage.ts';
import { chatQuickActionIcon } from '@/components/chatQuickActionIcon.tsx';
import { ChatBody } from '@/components/ChatBody.tsx';
import { NO_TURN_ACTIVITY, nextTurnActivity } from '@/lib/chatTurnActivity.ts';
import type { ChatActivityStep } from '@wundo/ui';
import { ChatNudge } from '@/components/ChatNudge.tsx';
import { ChatSidebar } from '@/components/ChatSidebar.tsx';

const NO_STEPS: readonly ChatActivityStep[] = [];

/**
 * Why a file was not taken, said in a way the user can do something about. The
 * limits come from the constants the backend enforces, so the sentence cannot
 * end up promising a cap nobody keeps.
 */
function attachmentProblem(problem: ChatAttachmentProblem): string {
  switch (problem) {
    case 'type':
      return m.chat_attachment_type();
    case 'size':
      return m.chat_attachment_size({
        file_size: formatFileSize(MAX_FILE_BYTES),
        image_size: formatFileSize(MAX_IMAGE_BYTES),
      });
    case 'too_many':
      return m.chat_attachment_too_many({ count: MAX_ATTACHMENTS_PER_MESSAGE });
    case 'unreadable':
      return m.chat_attachment_unreadable();
    case 'failed':
      return m.chat_attachment_failed();
  }
}

/**
 * Why nothing was said out loud in the end, with what to do about it: give
 * Wundo the microphone, say it again, or fall back to typing.
 */
function dictationProblemText(problem: DictationProblem): string {
  switch (problem) {
    case 'blocked':
      return m.chat_dictate_blocked();
    case 'empty':
      return m.chat_dictate_empty();
    case 'failed':
      return m.chat_dictate_failed();
  }
}

/**
 * The one thing a page may put in an empty drawer, in whichever shape it came.
 *
 * `intake` and `debrief` are questions Wundo asks — one before the conversation,
 * one after it; `recap` is what was said last time. They are drawn differently
 * and the first answer to each goes to its own door, but everything between
 * those two ends is the same: shown and not stored, standing only while the
 * drawer is blank, and answered exactly once per page.
 *
 * `nudge` is the odd one and ranks last: it is not about this page's meeting at
 * all but about the person reading it, so it opens no conversation, is answered
 * by nobody, and is simply taken away when it is refused or done.
 *
 * `key` is what "once" is counted by — the shape as well as the meeting, so an
 * offer that changes shape is a new opening rather than an answered one.
 */
type PageOpening =
  | {
      /**
       * One shape for both questions: a line the server wrote, drawn as Wundo's
       * own bubble. Only the door the first answer goes through tells them
       * apart, and that is the one thing the kind is read for.
       */
      kind: 'intake' | 'debrief';
      key: string;
      meetingId: string;
      conversationId: string | null;
      opener: string;
    }
  | {
      kind: 'recap';
      key: string;
      meetingId: string;
      conversationId: string | null;
      recap: ChatPageRecap;
    }
  | {
      kind: 'nudge';
      key: string;
      nudge: ChatPageNudge;
    };

/**
 * What this page opens an empty drawer with, or null where it opens with
 * nothing. Never more than one: the question wins over the summary, and both
 * win over a recommendation — what Wundo needs to know about the meeting on
 * screen is worth more than what the user has not tried yet.
 */
function pageOpening(offer: ChatPageOffer | null): PageOpening | null {
  if (offer?.intake) {
    return {
      kind: 'intake',
      key: `intake:${offer.intake.meetingId}`,
      meetingId: offer.intake.meetingId,
      conversationId: offer.intake.conversationId,
      opener: offer.intake.opener,
    };
  }

  if (offer?.debrief) {
    return {
      kind: 'debrief',
      key: `debrief:${offer.debrief.meetingId}`,
      meetingId: offer.debrief.meetingId,
      conversationId: offer.debrief.conversationId,
      opener: offer.debrief.opener,
    };
  }

  if (offer?.recap) {
    return {
      kind: 'recap',
      key: `recap:${offer.recap.meetingId}`,
      meetingId: offer.recap.meetingId,
      conversationId: offer.recap.conversationId,
      recap: offer.recap,
    };
  }

  if (offer?.nudge) {
    return {
      kind: 'nudge',
      key: `nudge:${offer.nudge.key}:${offer.nudge.meetingId ?? ''}`,
      nudge: offer.nudge,
    };
  }

  return null;
}

/**
 * How many sittings keep what arrived in them. Someone moving between
 * conversations in one visit touches a handful; past that the one left alone
 * longest is dropped, so a long session cannot grow without end.
 */
const LIVE_CONVERSATIONS_KEPT = 5;

/** Messages that arrived while the drawer was open, by the sitting they belong to. */
type LiveMessages = ReadonlyMap<string, ChatMessage[]>;

/**
 * A reply as it is being written: the message it is going to be, and what has
 * arrived of it so far. It is replaced by that message the moment it lands,
 * which is the only thing that says the reply is finished.
 */
interface StreamingReply {
  id: string;
  text: string;
}

/**
 * Replies still being written, by the sitting they belong to. A turn can write
 * more than one — a line before it goes off to look something up, then the
 * answer — and each is its own message, so each stays where it was written.
 */
type StreamingReplies = ReadonlyMap<string, readonly StreamingReply[]>;

export interface ChatPanelProps {
  open: boolean;
  /** Collapse to the strip on a desktop, close on a phone. */
  onDismiss: () => void;
  /** Open it again from the strip. */
  onExpand: () => void;
  userId: string | undefined;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** How wide the drawer stands. Left out for a drawer that is not draggable. */
  width?: number;
  onWidthChange?: (width: number) => void;
  onWidthReset?: () => void;
  stripRef?: Ref<HTMLButtonElement>;
}

/**
 * The chat drawer, and the conversation inside it.
 *
 * Whether a sitting is still running is the backend's judgement, asked each
 * time the drawer opens. From then on this names the conversation it is in, so
 * that moving around the app — or a long silence with the drawer open — cannot
 * start a new one underneath the user.
 *
 * What is read back and what arrives live are kept apart and joined at the
 * end: a reply is pushed the moment it is written and stored just after, so
 * the same message does not carry the same id on both routes.
 */
export function ChatPanel({
  open,
  onDismiss,
  onExpand,
  userId,
  expanded,
  onToggleExpanded,
  width,
  onWidthChange,
  onWidthReset,
  stripRef,
}: ChatPanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const impersonating = !!useImpersonatedBy();
  /*
   * What this page offers: its line, the things worth asking on it, and what to
   * say before anything has been said. The page says all of it, not the drawer
   * — the drawer has no idea what is behind it, and a stale invitation reads as
   * a mistake.
   */
  const offer = useChatPageOffer();

  const { data: state } = useQuery({
    queryKey: ['chatState'],
    queryFn: fetchChatState,
    /* Asked again on each open: a sitting can go quiet while the app is left alone. */
    enabled: open,
    staleTime: 0,
  });

  /* An explicit choice — a first message, or picking the last sitting back up. */
  const [chosen, setChosen] = useState<string | null>(null);
  /*
   * Every time someone asked for the caret without saying anything: a pill that
   * hands over to the composer rather than speaking for the user. Counted
   * rather than flagged, because asking twice has to land twice.
   */
  const [caretAsked, setCaretAsked] = useState(0);
  /*
   * Every time the drawer was sent to the page's opening question. The composer
   * rattles once per ask, so somebody who clicked a button on the page sees
   * where the answer to it goes.
   */
  const [attention, setAttention] = useState(0);
  /*
   * Standing on a conversation that does not exist yet, on purpose: the user
   * asked for a blank drawer, or the page put its opening question in front of
   * them. Nothing is created or closed by it — the sitting that was running is
   * still the running one until something is said.
   */
  const [startingNew, setStartingNew] = useState(false);
  const conversationId = startingNew ? null : (chosen ?? state?.conversation?.id ?? null);
  /*
   * The drawer stands on no conversation. One name for the three ways of
   * arriving there — asked for, offered, or none running — because what the
   * drawer does next hangs on the state and not on how it was reached: where
   * the first message goes, and whether a page's opener is in front of the user.
   */
  const blank = conversationId === null;

  /*
   * A conversation is read backwards a page at a time: the most recent page
   * first, because that is the one being carried on, and older ones only if
   * the reader goes looking for them.
   */
  const {
    data: pages,
    isError: historyFailed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['chatMessages', conversationId],
    queryFn: async ({ pageParam }) =>
      await fetchChatMessages(conversationId ?? '', pageParam),
    initialPageParam: 1,
    getNextPageParam: (page: ChatMessagePage) => page.older,
    enabled: open && conversationId !== null,
    /* Read again on every open: replies land while the drawer is away. */
    staleTime: 0,
  });

  /* Pages run newest to oldest; the drawer reads the other way. */
  const history = useMemo(
    () => [...(pages?.pages ?? [])].reverse().flatMap((page) => page.messages),
    [pages],
  );

  const [live, setLive] = useState<LiveMessages>(() => new Map());
  /*
   * Which sitting Wundo is answering, rather than whether it is answering at
   * all: a reply being written for the conversation the user just left must
   * not put dots under the one they moved to.
   */
  const [typingIn, setTypingIn] = useState<string | null>(null);
  /*
   * And which sitting a message failed to reach, for the same reason: the
   * warning is about that conversation, so it must not be left standing under
   * whichever one the user moves to next. A sitting that does not exist yet is
   * named the way the rest of the drawer names it.
   */
  const [failedIn, setFailedIn] = useState<string | null>(null);
  /*
   * And which sitting a message was too long for, kept apart from a failed
   * send: nothing was attempted here, so retrying in a moment is not the
   * advice — shortening it is.
   */
  const [tooLongIn, setTooLongIn] = useState<string | null>(null);
  /* A reply that landed while the drawer was away, still waiting to be seen. */
  const unread = useChatUnread();
  /* And, for the same reason again, which sitting a reply is being written in. */
  const [streaming, setStreaming] = useState<StreamingReplies>(() => new Map());
  /* What Wundo is doing for a reply, and did before the ones it began. This session only. */
  const [turnActivity, setTurnActivity] = useState(NO_TURN_ACTIVITY);
  const sendFailed = failedIn !== null && failedIn === (conversationId ?? 'new');
  const tooLong = tooLongIn !== null && tooLongIn === (conversationId ?? 'new');

  const messages = useMemo(() => {
    const arriving = conversationId ? (live.get(conversationId) ?? []) : [];
    const seen = new Set<string>();

    return [...history, ...arriving].filter((message) => {
      if (seen.has(message.id)) {
        return false;
      }
      seen.add(message.id);
      return true;
    });
  }, [history, live, conversationId]);

  /*
   * What is being written in the sitting on screen, minus anything already
   * here in full: the finished message carries the id its pieces did, so the
   * two would otherwise show as two for as long as one render lasts.
   */
  const streamingReplies = useMemo(() => {
    const arriving = conversationId ? (streaming.get(conversationId) ?? []) : [];

    return arriving.filter(
      (reply) => !messages.some((message) => message.id === reply.id),
    );
  }, [streaming, conversationId, messages]);

  /*
   * Dots are for a reply nobody can see yet. Once its words are arriving they
   * say it better than the dots do.
   */
  const typing =
    typingIn !== null && typingIn === conversationId && streamingReplies.length === 0;

  /*
   * One turn speaks at a time: while a message is on its way out or a reply is
   * on its way in, the composer is held — no typing, no files.
   */
  const responding = typing || streamingReplies.length > 0;

  const activitySteps = conversationId
    ? (turnActivity.pending.get(conversationId) ?? NO_STEPS)
    : NO_STEPS;

  /*
   * Appends to whichever sitting the message names, leaving the others as they
   * were: a reply for the conversation the user has just left must not take
   * down what arrived in the one they are reading, and is still theirs to find
   * when they go back to it.
   */
  const append = useCallback((message: ChatMessage) => {
    setLive((current) => {
      const held = current.get(message.conversation_id) ?? [];

      if (held.some((one) => one.id === message.id)) {
        return current;
      }

      const next = new Map(current);
      /* Re-inserting moves the sitting to the end, so the quietest drops first. */
      next.delete(message.conversation_id);
      next.set(message.conversation_id, [...held, message]);

      if (next.size > LIVE_CONVERSATIONS_KEPT) {
        const quietest = next.keys().next().value;
        if (quietest !== undefined) {
          next.delete(quietest);
        }
      }

      return next;
    });
  }, []);

  /**
   * Adds to the reply being written in the sitting it names, or starts one. A
   * piece is only ever the next bit of the message it names, so a piece for
   * another message begins one — under whatever was written before it, which
   * stays where it is until the finished messages arrive.
   */
  const appendDelta = useCallback(
    (conversation: string, messageId: string, text: string) => {
      setStreaming((current) => {
        const held = current.get(conversation) ?? [];
        const writing = held.at(-1);
        const next = new Map(current);

        next.set(
          conversation,
          writing && writing.id === messageId
            ? [...held.slice(0, -1), { id: writing.id, text: writing.text + text }]
            : [...held, { id: messageId, text }],
        );

        return next;
      });
    },
    [],
  );

  const onEvent = useCallback(
    (event: ChatEvent) => {
      setTurnActivity((current) => nextTurnActivity(current, event));

      if (event.type === 'activity') {
        return;
      }

      if (event.type === 'typing') {
        setTypingIn(event.conversation_id);
        return;
      }

      if (event.type === 'delta') {
        appendDelta(event.conversation_id, event.message_id, event.text);
        return;
      }

      /*
       * Nobody is looking, so the strip holds the news until somebody is. Asked
       * of the drawer's own store rather than of this component's props: the
       * socket is subscribed with this handler, and rebuilding it every time
       * the drawer opens would drop the line it is listening on.
       */
      if (event.message.author === 'wundo' && currentChatDrawerState() !== 'open') {
        markChatUnread();
      }

      /* A notice about files arrives mid-turn; the turn itself is still going. */
      if (event.message.notice) {
        append(event.message);
        void reportChatReceipt(event.message.conversation_id, 'delivered');
        return;
      }

      /* Only the conversation that was waiting stops waiting. */
      setTypingIn((current) =>
        current === event.message.conversation_id ? null : current,
      );
      /*
       * This message is here in full now, and so is everything written before
       * it. What comes after is still being written, and stays. A message that
       * finishes nothing on the list leaves nothing on it worth keeping.
       */
      setStreaming((current) => {
        const held = current.get(event.message.conversation_id);

        if (!held) {
          return current;
        }

        const finished = held.findIndex((reply) => reply.id === event.message.id);
        const writing = finished === -1 ? [] : held.slice(finished + 1);
        const next = new Map(current);

        if (writing.length === 0) {
          next.delete(event.message.conversation_id);
        } else {
          next.set(event.message.conversation_id, writing);
        }

        return next;
      });
      append(event.message);

      /* It is in the browser now, whether or not anyone is looking. */
      if (event.message.author === 'wundo') {
        void reportChatReceipt(event.message.conversation_id, 'delivered');
        /*
         * The turn may have written on the page behind the drawer — the intake
         * puts a whole preparation there. Nothing tells that page so, and a
         * finished reply is the moment it could have happened. Only a page that
         * is open answers this; one that is not gets read fresh anyway.
         */
        void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
        void queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] });
      }
    },
    [append, appendDelta, queryClient],
  );

  /*
   * The line was down, so anything said over it was missed. Reading the
   * conversation back is the only way to find out what — and what arrived
   * live is dropped rather than kept alongside it, because a message pushed
   * before it was stored does not carry the id it will be read back under,
   * and the two would show as two. A reply that was half written goes with
   * them: what it got to is on its row, so reading back is what recovers it.
   *
   * Wundo is no longer assumed to be answering: after the refetch either the
   * reply is there, or a fresh typing event says it is still coming.
   */
  const onReconnect = useCallback(() => {
    setLive(new Map());
    setStreaming(new Map());
    setTurnActivity(NO_TURN_ACTIVITY);
    setTypingIn(null);

    void queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
    void queryClient.invalidateQueries({ queryKey: ['chatState'] });
  }, [queryClient]);

  useChatSocket(userId, onEvent, onReconnect);

  /*
   * A reply that never comes — the turn failed, or the socket missed it —
   * would otherwise leave the dots bouncing for the rest of the session. Long
   * enough that a slow turn is never cut short; short enough that a dead one
   * does not look alive.
   */
  useEffect(() => {
    if (!typingIn) {
      return;
    }

    const timer = setTimeout(() => setTypingIn(null), TYPING_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [typingIn]);

  /*
   * Read is the drawer open, in front of someone, with something in it — not
   * merely a conversation loaded. A drawer left open on a hidden tab is not
   * being read, so the tab coming back into view counts as a fresh look.
   */
  const lastMessageId = messages.at(-1)?.id;

  useEffect(() => {
    if (!open || !conversationId || !lastMessageId) {
      return;
    }

    function reportIfVisible() {
      if (document.visibilityState === 'visible' && conversationId) {
        void reportChatReceipt(conversationId, 'read');
      }
    }

    reportIfVisible();
    document.addEventListener('visibilitychange', reportIfVisible);

    return () => document.removeEventListener('visibilitychange', reportIfVisible);
  }, [open, conversationId, lastMessageId]);

  const attachments = useChatAttachments();
  /* Only files that finished uploading can be named; the composer holds the send until they have. */
  const attachmentIds = attachments.pending.flatMap((one) => one.id ?? []);

  /*
   * The openings that have been taken up here, by their key. From that moment
   * there is a conversation about the meeting, and the page goes on offering
   * the same opening only because what would stop it — a preparation written,
   * a verslag that stops existing — has nothing to do with answering. So this
   * is what says it has been put, and stops it being put a second time.
   */
  const [openingAnswered, setOpeningAnswered] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /*
   * What the page has ready for a drawer with nothing in it yet: its opening
   * question, what was said in the previous conversation, or the one thing
   * worth recommending to somebody still finding their way. Never two — a
   * meeting has a question only when there is nothing to prepare from, and a
   * previous conversation is something to prepare from — so the drawer treats
   * them as one thing that arrives in three shapes.
   *
   * The key names the page and the shape together, so an offer that changes
   * shape is a different opening rather than an answered one.
   */
  const opening = pageOpening(offer);

  /*
   * The nudges taken off this drawer by hand or by being taken up. The page
   * goes on offering the same one — a refusal is counted, not obeyed until the
   * third — so this is what makes the ✕ mean anything on the screen it was
   * clicked on.
   */
  const [nudgesSettled, setNudgesSettled] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /*
   * The two openings that create a conversation, apart from the one that does
   * not. Everything below about answering, about where the first message goes
   * and about standing the drawer aside is about these two only: a nudge is a
   * card with a button, and answering it is not a thing anybody can do.
   */
  const conversational = opening !== null && opening.kind !== 'nudge' ? opening : null;

  /*
   * The recommendation to draw, or null. Only in a drawer that is blank and
   * stays blank: it is what stands there while there is nothing to read, and
   * the moment there is something it has no business being on screen.
   */
  const nudge =
    opening?.kind === 'nudge' && blank && !nudgesSettled.has(opening.key)
      ? opening
      : null;

  /*
   * The opening as it is being answered right now, or null.
   *
   * Only while the drawer is blank: answering creates a conversation, and from
   * that moment what was drawn is in it as a real message. So this both draws
   * the bubble and decides where the next message goes — one condition, because
   * they are one thing. Everything after the first answer is an ordinary send,
   * and reading an older conversation back is an ordinary chat.
   *
   * And only while the page names no conversation for it: the server saying
   * where the answering is already happening is what keeps a reload, or a
   * second visit, from putting the same thing up again and starting a second
   * conversation about one meeting.
   */
  const answeringOpening =
    conversational !== null &&
    conversational.conversationId === null &&
    blank &&
    !openingAnswered.has(conversational.key) &&
    userId !== undefined
      ? { ...conversational, userId }
      : null;

  const send = useMutation({
    mutationFn: async ({
      text,
      source,
    }: {
      text: string;
      source?: OnboardingNudgeMessageSource;
    }) =>
      answeringOpening
        ? answeringOpening.kind === 'intake'
          ? await sendMeetingIntake(
              answeringOpening.userId,
              answeringOpening.meetingId,
              text,
              attachmentIds,
            )
          : answeringOpening.kind === 'debrief'
            ? await sendMeetingDebrief(
                answeringOpening.userId,
                answeringOpening.meetingId,
                text,
                attachmentIds,
              )
            : await sendMeetingRecap(
                answeringOpening.userId,
                answeringOpening.meetingId,
                text,
                attachmentIds,
              )
        : startingNew
          ? await sendChatMessage(text, {
              startNew: true,
              attachmentIds,
              source,
            })
          : await sendChatMessage(text, {
              conversationId: conversationId ?? undefined,
              attachmentIds,
              source,
            }),
    onSuccess: (response) => {
      setFailedIn(null);
      setTooLongIn(null);
      setTypingIn(response.conversation_id);
      setStartingNew(false);
      attachments.clear();
      append(response.message);

      /*
       * It has been taken up; the meeting has its conversation now. The page
       * beside the drawer is told so: what it knows about the opening is what
       * decides whether it keeps asking after the verslag being written — and
       * a page still holding the unanswered question would never start.
       */
      if (answeringOpening) {
        setOpeningAnswered((current) => new Set(current).add(answeringOpening.key));
        void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
      }

      if (response.conversation_id !== conversationId) {
        /* The first message of a sitting is what creates it. */
        setChosen(response.conversation_id);
      }

      /* Speaking changes both which sitting is current and what the history says. */
      void queryClient.invalidateQueries({ queryKey: ['chatState'] });
      void queryClient.invalidateQueries({ queryKey: ['chatHistory'] });
    },
    onError: () => {
      setFailedIn(conversationId ?? 'new');
      setTypingIn(null);
    },
  });

  /*
   * A pasted verslag can be longer than one message may carry. Refused here
   * rather than by the server, so the drawer can say what is wrong instead of
   * calling it a send that did not go through.
   */
  const onSend = useCallback(
    (text: string) => {
      if (text.trim().length > MAX_CHAT_MESSAGE_LENGTH) {
        setTooLongIn(conversationId ?? 'new');
        return;
      }

      setTooLongIn(null);
      send.mutate({ text });
    },
    [conversationId, send],
  );

  /*
   * Why the last thing said out loud never became a message, or null. It
   * stands in the same place a refused file does — under the conversation,
   * above the composer — because it is the same kind of news.
   */
  const [dictationProblem, setDictationProblem] = useState<DictationProblem | null>(null);

  /*
   * What was dictated is an ordinary message: it goes out the moment it comes
   * back, without passing through the field. Speaking is saying something, and
   * a transcript sitting in the composer waiting to be pressed would be a
   * draft nobody asked for.
   */
  const onDictated = useCallback(
    (text: string) => {
      setDictationProblem(null);
      send.mutate({ text });
    },
    [send],
  );

  const dictation = useDictation({
    onText: onDictated,
    onProblem: setDictationProblem,
  });

  /*
   * The devices as the menu lists them. A device the browser will not name yet
   * is still a device that can be chosen, so it is numbered — the same way the
   * practice conversation numbers them.
   */
  const microphones = useMemo(
    () =>
      audioDeviceOptions(dictation.microphones, (index) =>
        m.chat_dictate_microphone_unnamed({ index }),
      ).map(({ value, label }) => ({ id: value, label })),
    [dictation.microphones],
  );

  /**
   * Say something on a nudge's behalf, with what it was written on it.
   *
   * The tag is the whole exit for the two nudges that are questions: asking
   * Wundo something leaves no row anywhere, so the button that asked says so
   * and the backend closes the nudge on that.
   */
  const sendFromNudge = useCallback(
    (text: string, source?: OnboardingNudgeMessageSource) =>
      send.mutate({ text, source }),
    [send],
  );

  const loadOlder = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const openFromHistory = useCallback((id: string) => {
    setChosen(id);
    setStartingNew(false);
  }, []);

  /*
   * Clears the drawer and has the next message open a conversation. Nothing is
   * created or closed until something is said, so changing your mind costs
   * nothing — the conversation you left is still the running one.
   */
  const startNewConversation = useCallback(() => {
    setStartingNew(true);
    setChosen(null);
    setLive(new Map());
    setStreaming(new Map());
    setTypingIn(null);
    /* A blank drawer is not the sitting anything failed to reach. */
    setFailedIn(null);
    setTooLongIn(null);
  }, []);

  /**
   * Stands the drawer where a page's opening belongs.
   *
   * Two ways of belonging, one for each answer the page can give. With a
   * conversation named it has been put and taken up already, so that is the
   * place: it is opened the way any conversation in the history is. With none,
   * it is still unanswered, and the place for it is a drawer with nothing in it
   * — created by nobody until something is said.
   */
  const standOnOpening = useCallback(
    (conversation: string | null) => {
      if (conversation === null) {
        startNewConversation();
        return;
      }

      openFromHistory(conversation);
    },
    [openFromHistory, startNewConversation],
  );

  /*
   * Which page's opening has already been put in front of the user. Cleared as
   * soon as no page is offering one, so coming back to the page shows it again
   * — while it keeps offering the same one, the drawer stands aside exactly
   * once.
   */
  const openerShownFor = useRef<string | null>(null);
  const openingKey = conversational?.key ?? null;
  const openingConversationId = conversational?.conversationId ?? null;

  /*
   * A page with an opening gets the drawer it needs for it, even where a
   * conversation from this morning is still running — it is about the page the
   * user just opened, and answering it in yesterday's sitting would be
   * answering it in the wrong place.
   *
   * The same stepping aside as "Nieuwe chat": nothing is created and nothing is
   * closed, so the sitting that was running is still there in the history, and
   * one click brings it back. Once per page, so closing it is closing it — and
   * never for one already answered here.
   */
  useEffect(() => {
    if (openingKey === null) {
      openerShownFor.current = null;
      return;
    }

    if (openingKey === openerShownFor.current || openingAnswered.has(openingKey)) {
      return;
    }

    openerShownFor.current = openingKey;
    standOnOpening(openingConversationId);
  }, [openingKey, openingConversationId, openingAnswered, standOnOpening]);

  /**
   * Takes the user to this page's opening question: the place first, then the
   * caret, then the flash that says both have moved.
   *
   * What is written next belongs to the conversation about this page, not to
   * the one the drawer had stepped away to.
   */
  const goToOpening = useCallback(() => {
    standOnOpening(openingConversationId);
    setCaretAsked((asked) => asked + 1);
    setAttention((shown) => shown + 1);
  }, [standOnOpening, openingConversationId]);

  /*
   * The same thing asked for from the page beside the drawer, where the button
   * that asks has nothing else to show for itself: on a desktop the drawer is
   * already standing open.
   *
   * Counted against what the drawer has answered rather than run on every
   * change, or the conversation arriving from the server would flash the panel
   * at somebody who asked for nothing.
   */
  const openingAsked = useChatOpeningAsked();
  const answeredAsk = useRef(openingAsked);
  /* Words a page put in the composer for the user, unsent. */
  const draft = useChatDraft();

  useEffect(() => {
    if (openingAsked === answeredAsk.current) return;
    answeredAsk.current = openingAsked;
    goToOpening();
  }, [openingAsked, goToOpening]);

  /*
   * A nudge nobody has ever been shown gets the drawer opened for it, once.
   *
   * Whether it is the first time is the server's answer, not the browser's: it
   * stamps the showing as it hands the nudge over, so the same nudge on the
   * next page comes back saying it is no longer new and the drawer stays
   * exactly as the user left it. Nothing else about the drawer is touched —
   * this opens it, and never closes it.
   *
   * Held by the nudge's own key rather than by "has a first-time nudge": the
   * page asks the server for one and publishes the answer when it lands, which
   * can be any number of renders after this drawer mounted, and the nudge that
   * arrives is what has to open the drawer — not the moment of arriving. Keyed,
   * a second nudge later in the same visit still opens it, and the one already
   * opened for is left alone, so collapsing it stays collapsed.
   */
  const firstTimeNudgeKey = nudge?.nudge.firstTime === true ? nudge.key : null;
  const nudgeOpenedFor = useRef<string | null>(null);

  useEffect(() => {
    if (firstTimeNudgeKey === null || firstTimeNudgeKey === nudgeOpenedFor.current) {
      return;
    }

    /* Stamped first: it has had its once, whatever the drawer was already doing. */
    nudgeOpenedFor.current = firstTimeNudgeKey;
    setChatDrawerState('open');
  }, [firstTimeNudgeKey]);

  /**
   * What a pill does when it is clicked.
   *
   * It says the words on it, unless the page said otherwise: hand the caret to
   * the user, or first stand the drawer where the page's opening question
   * belongs — the way back for someone who read an older conversation in the
   * meantime.
   */
  const runQuickAction = useCallback(
    (action: ChatQuickActionOffer) => {
      switch (action.behavior ?? 'send') {
        case 'send':
          onSend(action.text);
          return;
        case 'focus':
          setCaretAsked((asked) => asked + 1);
          return;
        case 'intake':
          goToOpening();
      }
    },
    [onSend, goToOpening],
  );

  const isEmpty = messages.length === 0;
  /*
   * Only what the page itself offers (DEV-16). Pills start a conversation, so
   * they go once the user has said something, the send in flight included; on
   * a phone they would otherwise sit above the keyboard. An intake pill stays:
   * it is the way back to the page's question from whatever is on screen.
   */
  const userHasSpoken =
    send.isPending || messages.some((message) => message.author === 'user');
  const quickActions = (offer?.quickActions ?? []).filter(
    (action) => !userHasSpoken || action.behavior === 'intake',
  );

  return (
    <ChatDrawer
      open={open}
      title={m.chat_title()}
      subtitle={offer?.line}
      closeLabel={m.chat_close()}
      collapseLabel={m.chat_collapse_drawer()}
      stripLabel={m.chat_strip()}
      resizeLabel={m.chat_resize()}
      onDismiss={onDismiss}
      onExpand={onExpand}
      width={width}
      onWidthChange={onWidthChange}
      onWidthReset={onWidthReset}
      stripRef={stripRef}
      unread={unread}
      unreadLabel={m.chat_unread()}
      expanded={expanded}
      /*
       * Only in full screen, which is also the only place the history is
       * reachable from: the docked column is too narrow to spend on a list.
       */
      sidebar={
        <ChatSidebar
          currentId={conversationId}
          onOpen={openFromHistory}
          onNew={startNewConversation}
        />
      }
      action={
        <>
          {/* Full screen has the sidebar's "Nieuwe chat"; two doors to one room confuse. */}
          {!expanded && (
            <IconButton
              aria-label={m.chat_new()}
              onClick={startNewConversation}
              disabled={isEmpty}
            >
              <SquarePen size={16} aria-hidden='true' />
            </IconButton>
          )}
          <IconButton
            aria-label={expanded ? m.chat_collapse() : m.chat_expand()}
            onClick={onToggleExpanded}
            className='max-mobile:hidden'
          >
            {expanded ? (
              <Minimize2 size={16} aria-hidden='true' />
            ) : (
              <Maximize2 size={16} aria-hidden='true' />
            )}
          </IconButton>
        </>
      }
    >
      <ChatBody
        /* Another conversation is another read: it opens at its own end. */
        key={conversationId ?? 'new'}
        expanded={expanded}
        messages={messages}
        streaming={streamingReplies}
        typing={typing}
        activitySteps={activitySteps}
        activityPinned={turnActivity.pinned}
        error={
          dictationProblem
            ? dictationProblemText(dictationProblem)
            : attachments.problem
              ? attachmentProblem(attachments.problem)
              : tooLong
                ? m.chat_too_long({
                    count: MAX_CHAT_MESSAGE_LENGTH.toLocaleString(getLocale()),
                  })
                : sendFailed
                  ? m.chat_send_failed()
                  : historyFailed
                    ? m.chat_load_failed()
                    : null
        }
        onSuggestion={onSend}
        sending={send.isPending}
        hasOlder={hasNextPage}
        loadingOlder={isFetchingNextPage}
        onLoadOlder={loadOlder}
        /*
         * A link Wundo wrote to one of our own pages goes there in this tab,
         * through the router: the drawer rides along with the user, and the
         * conversation they are reading survives the move.
         */
        onNavigate={(path) => void navigate({ href: path })}
        /*
         * The one thing that stands in an empty drawer: what the page opens
         * with — the question about a meeting Wundo can neither prepare nor
         * write up on its own, the summary of the previous conversation, or,
         * failing both, the one
         * thing worth recommending to somebody still finding their way. Drawn,
         * not sent and not stored: saying something is what creates the
         * conversation, and the answer comes back with these same words in it,
         * written by the server.
         */
        empty={
          answeringOpening ? (
            <ChatMessageList>
              <ChatMessageBubble author='wundo' authorLabel={m.chat_author_wundo()}>
                {answeringOpening.kind !== 'recap' ? (
                  <ChatMarkdown>{answeringOpening.opener}</ChatMarkdown>
                ) : (
                  <ChatRecapCard
                    title={answeringOpening.recap.title}
                    text={answeringOpening.recap.text}
                    /*
                     * The verslag it was read from is a page of this same app,
                     * so this stays inside the router: following it must not
                     * throw the drawer, and the conversation in it, away.
                     */
                    link={
                      <Link
                        to='/meetings/$meetingId'
                        params={{ meetingId: answeringOpening.recap.linkMeetingId }}
                        search={{ tab: 'report' }}
                        className={chatCardLinkVariants()}
                      >
                        {answeringOpening.recap.linkText}
                      </Link>
                    }
                  />
                )}
              </ChatMessageBubble>
            </ChatMessageList>
          ) : nudge ? (
            /*
             * Not a bubble: Wundo has not said this, the page has. So it stands
             * on its own in the empty drawer, with its own way out — everything
             * else in here was asked for, and this was not.
             */
            <ChatNudge
              nudge={nudge.nudge}
              userId={userId}
              disabled={send.isPending || responding || impersonating}
              onSend={sendFromNudge}
              onSettled={() =>
                setNudgesSettled((current) => new Set(current).add(nudge.key))
              }
            />
          ) : undefined
        }
      />
      {/*
            Above the composer and outside the conversation, so they stay
            reachable once it has started — the question worth asking second is
            usually the same as the one worth asking first.
          */}
      {quickActions.length > 0 ? (
        <ChatQuickActions label={m.chat_quick_actions_label()} expanded={expanded}>
          {quickActions.map((action) => (
            <ChatQuickAction
              key={action.text}
              disabled={send.isPending || responding || impersonating}
              onClick={() => runQuickAction(action)}
            >
              {chatQuickActionIcon(action.icon)}
              {action.text}
            </ChatQuickAction>
          ))}
        </ChatQuickActions>
      ) : null}
      <ChatDrawerFooter expanded={expanded}>
        <ChatComposer
          placeholder={m.chat_placeholder()}
          sendLabel={m.chat_send()}
          onSend={onSend}
          disabled={send.isPending || responding}
          /*
           * The backend refuses a message from an impersonated session; this
           * is only manners, so nobody types one out first. Reading back is
           * untouched — seeing the conversation is what "Login as" is for.
           */
          locked={impersonating}
          /*
           * Every moment someone is about to write: the drawer opening, a
           * conversation started, one picked up out of the history — and a pill
           * that handed the turn straight over.
           */
          focusOn={`${open}:${conversationId ?? 'new'}:${caretAsked}`}
          draft={draft}
          attention={attention}
          attachments={attachments.pending.map((one) => ({
            id: one.localId,
            kind: one.kind,
            name: one.name,
            detail: formatFileSize(one.size),
            uploading: one.id === undefined,
          }))}
          onAttach={attachments.add}
          onRemoveAttachment={attachments.remove}
          canAttach={attachments.canAttach}
          accept={CHAT_UPLOAD_ACCEPT}
          /*
           * No microphone where there is nothing to record with, and none for
           * an impersonated session: dictating is speaking, and the backend
           * refuses that in someone else's name for the same reason it refuses
           * the message that would follow it.
           */
          dictation={
            dictation.supported && !impersonating
              ? {
                  state: dictation.state,
                  seconds: dictation.seconds,
                  onStart: () => {
                    setDictationProblem(null);
                    dictation.start();
                  },
                  onStop: dictation.stop,
                  onCancel: dictation.cancel,
                  onOpenOptions: dictation.requestMicrophones,
                  microphones,
                  microphoneId: dictation.microphoneId,
                  onChooseMicrophone: dictation.chooseMicrophone,
                }
              : undefined
          }
          labels={{
            attach: m.chat_attach(),
            removeAttachment: m.chat_attachment_remove(),
            dictate: m.chat_dictate(),
            dictateStop: m.chat_dictate_stop(),
            dictateSending: m.chat_dictate_sending(),
            dictateCancel: m.chat_dictate_cancel(),
            dictateMicrophone: m.chat_dictate_microphone(),
            dictateOptions: m.chat_dictate_options(),
          }}
        />
        <ChatDisclaimer>
          {impersonating ? m.chat_impersonating() : m.chat_disclaimer()}
        </ChatDisclaimer>
      </ChatDrawerFooter>
    </ChatDrawer>
  );
}
